import React from "react";

import makeStyles from "@material-ui/core/styles/makeStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import useTheme from "@material-ui/core/styles/useTheme";
import { Theme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Image from "next/image";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import { useRouter } from "next/router";
import Link from "next/link";
import dynamic from "next/dynamic";
import useTranslation from "next-translate/useTranslation";
import useCompleoCareerPageContext from "../../useCompleoCareerPageContext";
import toLower from "lodash/toLower";
import PreloadImage from "./PreloadImage";

interface IMainAreaProps {
    key: string;
    name: string;
    metadata: any;
    careerPageClasses: any;
}

const MainArea = ({ name, metadata, careerPageClasses }: IMainAreaProps) => {
    const [data] = useCompleoCareerPageContext();
    const { moduleName, uniqueLink, isCustomDomain } = data;
    const { t } = useTranslation(moduleName);
    const items: any[] = metadata.items;
    const backgroundObj = items.filter(
        (i) => i.elementType === "image" || i.elementType === "video"
    )[0];
    const backgroundUrl = backgroundObj.srcUrl;
    const titleStyle = metadata.customStyle?.filter(
        (fc: any) => fc.name === "heading"
    )[0];
    const subtitleStyle = metadata.customStyle?.filter(
        (fc: any) => fc.name === "subheading"
    )[0];

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                height:
                    backgroundObj.size && backgroundObj.size === "small"
                        ? "35vh"
                        : "80vh",
                minHeight:
                    backgroundObj.size && backgroundObj.size === "small"
                        ? 250
                        : 600,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
            },
            videoRoot: {
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                zIndex: 1,
                overflow: "hidden",
                "&:before": {
                    content: '""',
                    position: "absolute",
                    top: "0",
                    left: "0",
                    righ: "0",
                    width: "100%",
                    height: "100%!important",
                    background: "rgba(35,31,31,0.5)",
                    zIndex: "-1",
                },
                "& video": {
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                },
            },
            imageRoot: {
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                zIndex: 1,
            },
            contentRoot: {
                zIndex: 2,
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
                flexDirection: "column",
                display: "flex",
            },
            title: {
                color:
                    titleStyle?.fontColor ?? theme.palette.primary.contrastText,
                fontFamily: theme.typography.h1.fontFamily,
                fontSize: titleStyle?.fontSize ?? theme.typography.h2.fontSize, // "1.5rem",
                fontWeight: theme.typography.h2.fontWeight,
                lineHeight: theme.typography.h2.lineHeight,
                [theme.breakpoints.up("sm")]: {
                    fontSize:
                        titleStyle?.fontSize ?? theme.typography.h1.fontSize,
                    fontWeight: theme.typography.h1.fontWeight,
                    lineHeight: theme.typography.h1.lineHeight,
                },
                textAlign: "center",
                flexWrap: "wrap",
            },
            subtitle: {
                color:
                    subtitleStyle?.fontColor ??
                    theme.palette.primary.contrastText,
                fontFamily: theme.typography.h3.fontFamily,
                fontSize:
                    subtitleStyle?.fontSize ?? theme.typography.h3.fontSize, // "1.5rem",
                fontWeight: theme.typography.h3.fontWeight,
                lineHeight: theme.typography.h3.lineHeight,
                [theme.breakpoints.up("sm")]: {
                    fontSize:
                        subtitleStyle?.fontSize ?? theme.typography.h2.fontSize,
                    fontWeight: theme.typography.h2.fontWeight,
                    lineHeight: theme.typography.h2.lineHeight,
                },
                textAlign: "center",
                flexWrap: "wrap",
            },
            inputRoot: {
                color: "inherits",
                width: "100%",
            },
            inputInput: {
                padding: theme.spacing(1, 1, 1, 0),
                paddingLeft: `calc(1em + ${theme.spacing(1)}px)`,
                transition: theme.transitions.create("width"),
                height: `calc(2rem + ${theme.spacing(1)}px)!important`,
                width: "100%",
            },
            search2: {
                backgroundColor: theme.palette.common?.white ?? "#ffffff",
                flexGrow: 1,
                marginTop: "10px",
                flexWrap: "nowrap",
            },
            buttonIcon: {
                color: theme.palette.common?.white ?? "#ffffff",
            },
            alignSelfEnd: {
                alignSelf: "flex-end",
            },
        })
    );

    const theme = useTheme();
    const classes = useStyles();
    const [openEditor, setEditorOpen] = React.useState(false);
    const router = useRouter();
    const [searchJobText, setSearchJobText] = React.useState("");

    const handleSearchJobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchJobText(e.target.value);
    };

    const handleSearchJobClick = (
        e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        e.preventDefault();
        router.push(
            `${toLower(uniqueLink)}/joblist?jobSearchText=${searchJobText}`
        );
    };

    const handleEditorOpen = () => {
        setEditorOpen(true);
    };

    const handleEditorClose = () => {
        setEditorOpen(false);
    };

    const headline = metadata.showText ? (
        <Grid item xs={12} sm={12} md={12} xl={12} style={{ zIndex: 2 }}>
            <Typography variant="h1" className={classes.title}>
                {t(`${name}_headerText`)}
            </Typography>
        </Grid>
    ) : undefined;

    const subHeadline =
        metadata.showText &&
        !t(`${name}_subheaderText`).includes("_subheaderText") ? (
            <Grid item xs={12} sm={12} md={12} xl={12} style={{ zIndex: 2 }}>
                <Typography variant="h2" className={classes.subtitle}>
                    {t(`${name}_subheaderText`)}
                </Typography>
            </Grid>
        ) : undefined;

    const searchBox = metadata.showSearchBox ? (
        <Grid item xs={8} sm={8} md={6} xl={6} style={{ zIndex: 2 }}>
            <Grid
                container
                direction="row"
                alignItems="center"
                justify="space-between"
                alignContent="stretch"
                className={classes.search2}
            >
                <Grid item style={{ width: "100%" }}>
                    <InputBase
                        placeholder={t(`${name}_searchBoxPlaceholder`)}
                        classes={{
                            root: classes.inputRoot,
                            input: classes.inputInput,
                        }}
                        inputProps={{
                            "aria-label": "search",
                        }}
                        onChange={handleSearchJobChange}
                    />
                </Grid>
                <Grid item>
                    <Link
                        href={
                            isCustomDomain
                                ? `/joblist?jobSearchText=${searchJobText}`
                                : `/${toLower(
                                      uniqueLink
                                  )}/joblist?jobSearchText=${searchJobText}`
                        }
                        passHref
                    >
                        <IconButton
                            edge="start"
                            aria-label="search job"
                            aria-haspopup="true"
                        >
                            <SearchIcon />
                        </IconButton>
                    </Link>
                </Grid>
            </Grid>
        </Grid>
    ) : undefined;

    const videoBackground =
        backgroundObj.elementType === "video" ? (
            <div className={classes.videoRoot}>
                <video
                    autoPlay={true}
                    loop={true}
                    playsInline={true}
                    muted={true}
                >
                    <source src={backgroundUrl}></source>
                </video>
            </div>
        ) : undefined;

    const imageBackground =
        backgroundObj.elementType === "image" ? (
            <div className={classes.imageRoot}>
                <PreloadImage src={backgroundUrl} />
                <Image
                    src={backgroundUrl}
                    alt={name}
                    layout="fill"
                    objectFit="cover"
                    quality={85}
                    priority={true}
                />
            </div>
        ) : undefined;

    return (
        <>
            <div className={classes.root}>
                <Grid
                    container
                    direction="row"
                    justify="center"
                    alignItems="center"
                >
                    {imageBackground}
                    {videoBackground}
                    {headline}
                    {subHeadline}
                    {searchBox}
                </Grid>
            </div>
        </>
    );
};

export default MainArea;
